import React, { Fragment } from 'react';
import MenuItem from './MenuItem.js';
import styled from '@emotion/styled';
import {
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Button,
} from '@chakra-ui/react';
import { useCallback } from 'react';

const MenuBar = ({ editor }) => {
  const StyledDivider = styled(Box)({
    '& .divider': {
      background: 'rgba(255,255,255, 0.25)',
      height: '1.25rem',
      marginLeft: '0.5rem',
      marginRight: '0.75rem',
      width: '1px',
    },
  });

  const dropdown = [
    {
      icon: 'h-1',
      title: 'Header',
      action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: () => editor.isActive('heading', { level: 1 }),
    },
    {
      icon: 'h-2',
      title: 'Title',
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: () => editor.isActive('heading', { level: 2 }),
    },
    {
      icon: 'h-3',
      title: 'Subtitle',
      action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: () => editor.isActive('heading', { level: 3 }),
    },
    {
      icon: 'h-4',
      title: 'Normal Format',
      action: () => editor.chain().focus().toggleHeading({ level: 4 }).run(),
      isActive: () => editor.isActive('heading', { level: 4 }),
    },
  ];

  const items = [
    {
      icon: 'bold',
      title: 'Bold',
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive('bold'),
    },
    {
      icon: 'italic',
      title: 'Italic',
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: () => editor.isActive('italic'),
    },
    {
      icon: 'strikethrough',
      title: 'Strike',
      action: () => editor.chain().focus().toggleStrike().run(),
      isActive: () => editor.isActive('strike'),
    },
    {
      icon: 'code-view',
      title: 'Code',
      action: () => editor.chain().focus().toggleCode().run(),
      isActive: () => editor.isActive('code'),
    },
    {
      icon: 'mark-pen-line',
      title: 'Highlight',
      action: () => editor.chain().focus().toggleHighlight().run(),
      isActive: () => editor.isActive('highlight'),
    },
    {
      type: 'divider',
    },
    {
      icon: 'paragraph',
      title: 'Paragraph',
      action: () => editor.chain().focus().setParagraph().run(),
      isActive: () => editor.isActive('paragraph'),
    },
    {
      icon: 'list-unordered',
      title: 'Bullet List',
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive('bulletList'),
    },
    {
      icon: 'list-ordered',
      title: 'Ordered List',
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.isActive('orderedList'),
    },
    {
      icon: 'list-check-2',
      title: 'Task List',
      action: () => editor.chain().focus().toggleTaskList().run(),
      isActive: () => editor.isActive('taskList'),
    },
    {
      icon: 'code-box-line',
      title: 'Code Block',
      action: () => editor.chain().focus().toggleCodeBlock().run(),
      isActive: () => editor.isActive('codeBlock'),
    },
    {
      type: 'divider',
    },
    {
      icon: 'double-quotes-l',
      title: 'Blockquote',
      action: () => editor.chain().focus().toggleBlockquote().run(),
      isActive: () => editor.isActive('blockquote'),
    },
    {
      icon: 'separator',
      title: 'Horizontal Rule',
      action: () => editor.chain().focus().setHorizontalRule().run(),
    },
    {
      type: 'divider',
    },
    {
      icon: 'text-wrap',
      title: 'Hard Break',
      action: () => editor.chain().focus().setHardBreak().run(),
    },
    {
      icon: 'format-clear',
      title: 'Clear Format',
      action: () => editor.chain().focus().clearNodes().unsetAllMarks().run(),
    },
    {
      type: 'divider',
    },
    {
      icon: 'arrow-go-back-line',
      title: 'Undo',
      action: () => editor.chain().focus().undo().run(),
    },
    {
      icon: 'arrow-go-forward-line',
      title: 'Redo',
      action: () => editor.chain().focus().redo().run(),
    },
    {
      type: 'divider',
    },
    {
      icon: 'book-line',
      title: 'Bible Passage',
      action: useCallback(() => {
        const previousVerse = editor.getAttributes('bibleVerse').verse;
        const verse = window.prompt('Bible Verse', previousVerse);
        if (verse === null) {
          return;
        }

        // empty
        if (verse === '') {
          editor
            .chain()
            .focus()
            .extendMarkRange('bibleVerse')
            .unsetBibleVerse()
            .run();

          return;
        }
        editor.commands.insertBibleVerse(verse);
      }, [editor]),
    },
    {
      icon: 'sticky-note-add-line',
      title: 'Add Notes',
      action: useCallback(() => {
        editor.commands.insertUserNotes();
      }, [editor]),
    },
    {
      icon: 'space',
      title: 'Fill in The Blank',
      action: useCallback(() => {
        const { from, to } = editor.state.selection;
        const textBlank = editor.state.doc.textBetween(from, to);
        if (textBlank === '') {
          return;
        }
        editor.commands.insertFillInBlank(textBlank);
        editor.view.dispatch(editor.view.state.tr.delete(from + 2, to + 2));
      }, [editor]),
    },
    {
      icon: 'delete-bin-line',
      title: 'Delete Note',
      action: useCallback(() => {
        if (editor.isActive('userNotes')) {
          editor.commands.deleteUserNotes();
        }
      }, [editor]),
      isActive: () => editor.isActive('userNotes'),
    },
  ];

  return (
    <div className="editor__header">
      <Popover trigger="hover">
        <PopoverTrigger>
          <Button
            variant="ghost"
            style={{ color: 'white', backgroundColor: 'black' }}
            _hover={{ color: 'black' }}
          >
            Text Style
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <PopoverBody>
            {dropdown.map((item, index) => (
              <Box key={index} onClick={item.action}>
                {item.title}
              </Box>
            ))}
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <StyledDivider>
        <div className="divider" />
      </StyledDivider>
      {items.map((item, index) => (
        <Fragment key={index}>
          {item.type === 'divider' ? (
            <StyledDivider>
              <div className="divider" />
            </StyledDivider>
          ) : (
            <MenuItem {...item} />
          )}
        </Fragment>
      ))}
    </div>
  );
};

export default MenuBar;
